import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { generateServiceSchema, generateOrganizationSchema, generateFAQSchema, generateBreadcrumbSchema, injectSchema } from '@/lib/swissSchema';
import { updateMetaTags } from '@/utils/seoCore';
import { generateHreflangLinks, updateHreflangLinks } from '@/lib/swissHreflang';
import { SwissLanguage, getCityDisplayName } from '@/lib/swissI18n';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Monitor, CheckCircle, Smartphone, Globe, Zap, Clock, TrendingUp } from 'lucide-react';

const WebdesignService = () => {
  const { city } = useParams();
  const { currentLanguage } = useLanguage();
  const language = currentLanguage as SwissLanguage;
  
  const cityDisplayName = city ? getCityDisplayName(language, city) : null;
  const isHubPage = !city;
  
  const content = {
    de: {
      title: isHubPage 
        ? 'Webdesign & Website Entwicklung – Moderne Websites, die verkaufen'
        : `Webdesign ${cityDisplayName} – In 14 Tagen live, SEO-ready`,
      description: isHubPage
        ? 'Professionelles Webdesign und Website-Entwicklung. Responsive, mehrsprachig, SEO-optimiert. Von der Konzeption bis zum Launch in 14 Tagen.'
        : `Moderne Website-Entwicklung in ${cityDisplayName}. Design + Technik + SEO aus einer Hand. Mehrsprachig, blitzschnell. Gratis Audit anfordern.`,
      h1: isHubPage
        ? 'Webdesign & Website Entwicklung'
        : `Webdesign ${cityDisplayName}`,
      subtitle: isHubPage
        ? 'Websites, die verkaufen – responsive, mehrsprachig, SEO-ready'
        : `In 14 Tagen live – Webdesign aus ${cityDisplayName}`,
      hero: 'Ihre Website ist Ihr wichtigster Verkäufer. Wir entwickeln moderne, responsive Websites, die nicht nur schön aussehen, sondern messbar mehr Kunden gewinnen.',
      
      packages: [
        {
          name: 'Business Starter',
          price: 'CHF 3\'900',
          features: [
            'Bis zu 8 Seiten',
            'Responsive Design',
            'SEO-Grundoptimierung',
            'Kontaktformular',
            'Google Analytics Setup',
            '30 Tage Support'
          ]
        },
        {
          name: 'Business Growth',
          price: 'CHF 6\'900',
          features: [
            'Bis zu 15 Seiten',
            'Mehrsprachig (DE/FR/EN)',
            'Advanced SEO',
            'CMS Integration',
            'Lead Magnets',
            'E-Commerce Ready',
            '90 Tage Support'
          ],
          popular: true
        },
        {
          name: 'Enterprise',
          price: 'CHF 12\'500',
          features: [
            'Unbegrenzte Seiten',
            'Vollständig mehrsprachig',
            'Custom Funktionen',
            'API Integrationen',
            'Performance Monitoring',
            'Wartungsvertrag',
            '12 Monate Support'
          ]
        }
      ],
      
      process: [
        { step: 1, title: 'Discovery & Strategie', desc: 'Ziele, Zielgruppe und Wettbewerb analysieren' },
        { step: 2, title: 'Design & Prototyping', desc: 'Wireframes und visuelles Design erstellen' },
        { step: 3, title: 'Entwicklung & CMS', desc: 'Responsive Programmierung und Content-Management' },
        { step: 4, title: 'SEO & Testing', desc: 'Suchmaschinenoptimierung und Quality Assurance' },
        { step: 5, title: 'Launch & Training', desc: 'Go-Live und Team-Schulung' }
      ],
      
      features: [
        { icon: Smartphone, title: 'Mobile-First Design', desc: 'Optimiert für alle Geräte und Bildschirmgrössen' },
        { icon: Globe, title: 'Mehrsprachig', desc: 'DE/FR/IT/EN mit SEO-optimierten hreflang Tags' },
        { icon: Zap, title: 'Performance', desc: 'Lighthouse Score >90, Core Web Vitals optimiert' },
        { icon: TrendingUp, title: 'Conversion-optimiert', desc: 'Lead-Generierung und Sales-Funnel Integration' }
      ],
      
      faq: [
        {
          question: `Was kostet Webdesign in ${cityDisplayName || 'der Schweiz'} für KMU?`,
          answer: 'Business-Websites ab CHF 3\'900. Mehrsprachige E-Commerce Lösungen ab CHF 6\'900. Enterprise-Projekte ab CHF 12\'500. Fixpreise, keine versteckten Kosten.'
        },
        {
          question: 'Wie schnell können wir live gehen?',
          answer: 'Standard-Websites in 14 Tagen. Komplexe E-Commerce Projekte in 4-6 Wochen. Agiler Prozess mit wöchentlichen Updates.'
        },
        {
          question: 'Was ist Multilingual SEO für die Schweiz?',
          answer: 'de-CH, fr-CH, it-CH Optimierung mit korrekten hreflang Tags. Lokale Suchmaschinenoptimierung für Zürich, Basel, Genf, Bern.'
        },
        {
          question: 'Welches CMS verwenden Sie?',
          answer: 'WordPress für Flexibilität, Webflow für Design-Focus, oder Custom React/Next.js für Performance. Je nach Anforderung.'
        },
        {
          question: 'Ist die Website GDPR/revFADP konform?',
          answer: 'Ja. Cookie-Consent, Datenschutz und alle revFADP Anforderungen (seit Sept 2023) sind standardmässig implementiert.'
        },
        {
          question: 'Bieten Sie auch Wartung und Updates?',
          answer: 'Ja. Wartungsverträge ab CHF 199/Monat. Security Updates, Content-Pflege und Performance Monitoring inklusive.'
        }
      ]
    }
  };

  const currentContent = content[language] || content.de;
  
  useEffect(() => {
    updateMetaTags({
      title: currentContent.title,
      description: currentContent.description,
      canonicalUrl: `https://www.future-level.ch/${language}/webdesign${city ? `/${city}` : ''}`,
      language: language,
      keywords: `Webdesign, Website Entwicklung, Responsive Design, ${cityDisplayName || 'Schweiz'}, CMS`
    });

    const hreflangLinks = generateHreflangLinks({
      currentLanguage: language,
      currentPath: `/webdesign${city ? `/${city}` : ''}`,
      availableLanguages: ['de', 'fr', 'it', 'en']
    });
    updateHreflangLinks(hreflangLinks);

    const orgSchema = generateOrganizationSchema({ language });
    const serviceSchema = generateServiceSchema({
      serviceName: 'Webdesign & Website Entwicklung',
      serviceSlug: 'webdesign',
      city,
      language,
      priceRange: 'CHF 3900-12500',
      description: currentContent.description,
      url: `https://www.future-level.ch/${language}/webdesign${city ? `/${city}` : ''}`
    });
    const breadcrumbSchema = generateBreadcrumbSchema(language, 'webdesign', city);
    const faqSchema = generateFAQSchema({ 
      questions: currentContent.faq, 
      language 
    });

    injectSchema({
      "@context": "https://schema.org",
      "@graph": [orgSchema, serviceSchema, breadcrumbSchema, faqSchema]
    });
  }, [language, city, cityDisplayName]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900">
      <Header />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-24 overflow-hidden">
          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-6 bg-blue-600/20 text-blue-200 border-blue-500/30">
                <Monitor className="w-4 h-4 mr-2" />
                Webdesign Experte {cityDisplayName && `• ${cityDisplayName}`}
              </Badge>
              
              <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                {currentContent.h1}
                <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent block">
                  {currentContent.subtitle}
                </span>
              </h1>
              
              <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
                {currentContent.hero}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white">
                  <Monitor className="w-5 h-5 mr-2" />
                  Kostenloses Website-Audit
                </Button>
                <Button size="lg" variant="outline" className="border-blue-500/30 text-blue-200 hover:bg-blue-900/20">
                  Portfolio ansehen
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-black/20">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                Warum unsere Websites funktionieren
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
              {currentContent.features.map((feature, index) => (
                <Card key={index} className="bg-gradient-to-b from-slate-800/50 to-slate-900/50 border-slate-700 text-center">
                  <CardContent className="p-6">
                    <feature.icon className="w-12 h-12 text-blue-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                    <p className="text-gray-300 text-sm">{feature.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Packages Section */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                Webdesign Pakete
              </h2>
              <p className="text-gray-300 text-lg max-w-2xl mx-auto">
                Von der einfachen Business-Website bis zur Enterprise-Lösung
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {currentContent.packages.map((pkg, index) => (
                <Card key={index} className={`relative bg-gradient-to-b from-slate-800/50 to-slate-900/50 border-slate-700 ${pkg.popular ? 'ring-2 ring-blue-500' : ''}`}>
                  {pkg.popular && (
                    <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white">
                      Beliebt
                    </Badge>
                  )}
                  <CardHeader>
                    <CardTitle className="text-white text-xl">{pkg.name}</CardTitle>
                    <CardDescription className="text-3xl font-bold text-blue-400">
                      {pkg.price}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {pkg.features.map((feature, fIndex) => (
                        <li key={fIndex} className="flex items-center text-gray-300">
                          <CheckCircle className="w-5 h-5 text-green-400 mr-3 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Button className="w-full mt-6 bg-blue-600 hover:bg-blue-700">
                      Angebot anfordern
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-20 bg-black/20">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                Von der Idee zur Website
              </h2>
              <p className="text-gray-300 text-lg">
                Unser bewährter 5-Schritte Prozess
              </p>
            </div>
            
            <div className="max-w-4xl mx-auto">
              {currentContent.process.map((step, index) => (
                <div key={index} className="flex items-start mb-8 last:mb-0">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold mr-6">
                    {step.step}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">{step.title}</h3>
                    <p className="text-gray-300">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-white text-center mb-12">
                Häufige Fragen
              </h2>
              
              <div className="space-y-6">
                {currentContent.faq.map((item, index) => (
                  <Card key={index} className="bg-slate-800/50 border-slate-700">
                    <CardContent className="p-6">
                      <h3 className="text-lg font-semibold text-white mb-3">{item.question}</h3>
                      <p className="text-gray-300">{item.answer}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-blue-600 to-cyan-600">
          <div className="container mx-auto px-6 text-center">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
                Bereit für Ihre neue Website?
              </h2>
              <p className="text-xl text-blue-100 mb-8">
                Kostenloses Website-Audit und Strategiegespräch{cityDisplayName && ` in ${cityDisplayName}`}.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100">
                  <Clock className="w-5 h-5 mr-2" />
                  Beratungstermin buchen
                </Button>
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                  <Monitor className="w-5 h-5 mr-2" />
                  Website-Audit starten
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default WebdesignService;